import React, { useEffect, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { Button, message } from 'antd';
import { PictureOutlined } from '@ant-design/icons';
import {
  DELETE_CONFIRM,
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';

import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { StoryGroupService } from 'services/StoryGroupService';

import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { StoryGroupForm } from './StoryGroupForm';
import { StoryGroupTable } from './StoryGroupTable';

function StoryGroup() {
  useDocumentTitle('Группа истории');
  const { setCount } = useHeaderChanger('Группа истории');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const { page, totalRows, perPage, setTotalRows, setPagination } =
    usePagination();

  useEffect(() => {
    fetchStoryGroups();
  }, [page, perPage]);

  const fetchStoryGroups = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await StoryGroupService.getStoryGroups(
        page,
        perPage
      );

      setItems(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await StoryGroupService.deleteStoryGroup(
        activeRecord.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchStoryGroups();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  const handleMove = () => {
    navigate({
      pathname: `/setting/mobile/stories`,
      search: `?${createSearchParams({ groupId: activeRecord.id })}`,
    });
  };

  const moveToStories = (
    <Button icon={<PictureOutlined />} onClick={handleMove}>
      Перейти к историям
    </Button>
  );

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="storygroups" />

      <StoryGroupTable
        items={items}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.title}
        pageId="storygroups"
        recordFields={[
          { label: 'Заголовок', key: 'title' },
          { label: 'Заголовок (каз.)', key: 'title_kz' },
          { label: 'Медиа', key: 'thumbnail' },
          { label: 'Медиа (каз.)', key: 'thumbnail_kz' },
          {
            label: 'Количество истории',
            key: 'stories_count',
            computedValue: `${activeRecord?.count}`,
          },
          { label: ' Порядок', key: 'position' },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText={DELETE_CONFIRM}
        extraActions={moveToStories}
      />

      <StoryGroupForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchStoryGroups}
        onClose={closeForm}
      />
    </>
  );
}

export { StoryGroup };
